import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Globe, RefreshCw, Users, CalendarDays, Mail, Phone, ExternalLink, CreditCard, AlertTriangle } from 'lucide-react';

const eur = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} €`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export default function SiteRequestsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/site-bookings');
      setBookings(res.data.items || []);
    } catch { /* silenzioso */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="site-bookings-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Globe className="h-6 w-6 text-sky-600" /> Prenotazioni dal sito
          </h1>
          <p className="text-sm text-slate-500 mt-1">Prenotazioni dirette arrivate dalla vetrina Holidayworld (già pagate online)</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="site-bookings-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {!loading && bookings.length === 0 && (
        <Card><CardContent className="py-12 text-center text-slate-400" data-testid="site-bookings-empty">
          Nessuna prenotazione dal sito al momento.
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {bookings.map((b) => (
          <Card key={b.id} data-testid={`site-booking-card-${b.id}`}>
            <CardContent className="py-4 px-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <Link to={`/properties/${b.property_id}/scheda`}
                      className="font-semibold text-slate-800 hover:text-sky-700 flex items-center gap-1"
                      data-testid="site-booking-property-link">
                      {b.property_name} <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                    </Link>
                    <span className="text-xs text-slate-400">{b.property_code}</span>
                  </div>
                  <div className="text-xs font-mono text-slate-400 mt-0.5">{b.booking_code}</div>
                  <div className="text-sm text-slate-600 mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                      {b.check_in} → {b.check_out}{b.nights ? ` · ${b.nights} notti` : ''}</span>
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" /> {b.guests_count}</span>
                  </div>
                </div>
                <div className="min-w-[220px] text-sm">
                  <div className="font-medium text-slate-700">{b.guest_name}</div>
                  <a href={`mailto:${b.guest_email}`} className="text-sky-700 hover:underline flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" /> {b.guest_email}</a>
                  {b.guest_phone && (
                    <a href={`tel:${b.guest_phone}`} className="text-slate-600 flex items-center gap-1 mt-0.5">
                      <Phone className="h-3.5 w-3.5" /> {b.guest_phone}</a>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-slate-800" data-testid="site-booking-total">{eur(b.guest_total)}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                    <CreditCard className="h-3 w-3" /> {eur(b.amount_collected)} incassati ({b.payment_type === 'full' ? 'totale' : 'acconto'})
                  </div>
                  <div className="flex items-center gap-1 justify-end mt-2">
                    {b.site_test && (
                      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200" data-testid="site-booking-test">
                        TEST
                      </Badge>
                    )}
                    {b.site_price_mismatch && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="site-booking-mismatch">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Verifica prezzo
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="site-booking-status">
                      Confermata · Pagata
                    </Badge>
                  </div>
                </div>
              </div>
              {b.special_requests && <div className="mt-2 text-sm text-slate-500 bg-slate-50 rounded px-3 py-2">📝 {b.special_requests}</div>}
              <div className="mt-2 text-xs text-slate-400">Ricevuta il {fmtDate(b.created_at)} · Rif. pagamento: {b.payment_reference || '—'}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
